/**
 * Reading rich text, whichever shape it was stored in.
 *
 * Every function here accepts BOTH the Tiptap `doc` object the seed scripts
 * write and the BlockNote `Block[]` array the admin editor saves. That is the
 * whole point of the module: before it existed, four bespoke sites hand-rolled
 * a walker that understood only the seeded shape, so every edit a client made
 * in the CMS was silently discarded (§6.11).
 *
 * The rule for anything added here: **it must be symmetric.** A helper that
 * reads one format and not the other reintroduces the defect in a new place.
 */
import type { RichNode, Run } from './types.js'

/** Containers a caller might hand us, in the order they appear in the wild. */
interface Wrapped {
  /** Tiptap/ProseMirror. */
  content?: unknown
  /** Seen in older stored payloads and honoured by the section renderer. */
  blocks?: unknown
}

/**
 * Top-level blocks, whichever wrapper the CMS stored them in.
 *
 * Accepts a bare array (BlockNote), `{type:'doc', content:[…]}` (Tiptap) and
 * `{blocks:[…]}` (older payloads — `cms-components/sections/rich-text.tsx` has
 * always read that one, so dropping it here would regress the template path).
 */
export function blocksOf(content: unknown): RichNode[] {
  if (Array.isArray(content)) return content as RichNode[]
  if (!content || typeof content !== 'object') return []
  const wrapped = content as Wrapped
  if (Array.isArray(wrapped.content)) return wrapped.content as RichNode[]
  if (Array.isArray(wrapped.blocks)) return wrapped.blocks as RichNode[]
  return []
}

/**
 * Is this value present, but in a shape we cannot walk?
 *
 * The distinction matters more than it looks. `bespoke-cms-site-engine.md` tells
 * loaders to fall back to compiled statics when a value is "missing or
 * unparseable", which makes an unreadable shape indistinguishable from no CMS
 * content at all — the page keeps serving plausible old prose and the client
 * reports "my edit didn't take". Absent is normal; unreadable is a defect and
 * must be loud.
 *
 * An HTML string is neither: it is a third, legitimately-handled legacy format
 * with its own render path, so it is not reported here.
 */
export function isUnreadable(content: unknown): boolean {
  if (content === null || content === undefined) return false
  if (typeof content === 'string') return false
  if (Array.isArray(content)) return false
  if (typeof content !== 'object') return true
  const wrapped = content as Wrapped
  return !Array.isArray(wrapped.content) && !Array.isArray(wrapped.blocks)
}

/** Flatten a node to its plain text. */
export function textOf(node: RichNode | undefined): string {
  if (!node) return ''
  if (node.type === 'text') return node.text ?? ''
  // A BlockNote `link` node holds its label in `content`, so plain recursion
  // already flattens it correctly.
  return (node.content ?? []).map(textOf).join('')
}

/** Does this content carry any non-empty text? */
export function hasText(content: unknown): boolean {
  if (typeof content === 'string') return content.trim().length > 0
  return blocksOf(content).map(textOf).join('').trim().length > 0
}

/** A link's target, from either format. Tiptap marks it, BlockNote names it. */
export function hrefOf(node: RichNode): string | undefined {
  if (node.type === 'link' && node.href) return node.href
  return node.marks?.find((m) => m.type === 'link')?.attrs?.href || undefined
}

/** Italic, from either format — Tiptap puts it in `marks`, BlockNote in `styles`. */
export function italicOf(node: RichNode): boolean {
  if (node.styles?.italic) return true
  return node.marks?.some((m) => m.type === 'italic') ?? false
}

/** Bold, from either format. */
export function boldOf(node: RichNode): boolean {
  if (node.styles?.bold) return true
  return node.marks?.some((m) => m.type === 'bold' || m.type === 'strong') ?? false
}

/**
 * A heading's level, from either format.
 *
 * Tiptap stores it on `attrs`, BlockNote on `props`. The kit's `headingsOf` read
 * only `props.level` and therefore matched no seeded heading at all — a live bug
 * this replaces rather than reproduces.
 */
export function levelOf(node: RichNode): number | undefined {
  const level = node.props?.level ?? node.attrs?.level
  return typeof level === 'number' && !Number.isNaN(level) ? level : undefined
}

/** Marks on a run, ignoring `text`, so adjacent runs can be compared. */
function sameMarks(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)])
  keys.delete('text')
  for (const key of keys) if (a[key] !== b[key]) return false
  return true
}

/**
 * A node flattened to runs of text, each carrying its marks.
 *
 * `project` narrows a run to just the marks a caller renders, and the merge
 * happens AFTER projection — so a caller that only cares about links gets one
 * span across a phrase whose italics change mid-way, instead of two visually
 * identical ones. Passing no projection keeps every mark.
 *
 * Marks inherit down the tree: Tiptap puts a link on the text node while
 * BlockNote wraps the text in a link node, and inheritance is what makes those
 * two produce the same runs.
 */
export function runsOf(node: RichNode | undefined): Run[]
export function runsOf<T extends { text: string }>(node: RichNode | undefined, project: (run: Run) => T): T[]
export function runsOf<T extends { text: string }>(
  node: RichNode | undefined,
  // eslint-disable-next-line no-restricted-syntax -- identity default, only reachable from the no-projection overload where T IS Run; the cast asserts what that overload already guarantees.
  project: (run: Run) => T = (run) => run as unknown as T
): T[] {
  const flat: Run[] = []

  const walk = (current: RichNode, inherited: Run) => {
    const marks: Run = {
      text: '',
      href: hrefOf(current) ?? inherited.href,
      italic: italicOf(current) || inherited.italic,
      bold: boldOf(current) || inherited.bold,
    }
    if (current.type === 'text') {
      const text = current.text ?? ''
      if (text) flat.push({ ...marks, text })
      return
    }
    for (const child of current.content ?? []) walk(child, marks)
  }
  if (node) walk(node, { text: '' })

  // Drop falsy marks before projecting, so `{ text }` and `{ text, italic:false }`
  // are the same object to a caller that spreads the result.
  const cleaned = flat.map((run) => {
    const out: Run = { text: run.text }
    if (run.href) out.href = run.href
    if (run.italic) out.italic = true
    if (run.bold) out.bold = true
    return out
  })

  return cleaned.map(project).reduce<T[]>((acc, run) => {
    const prev = acc[acc.length - 1]
    if (prev && sameMarks(prev, run)) prev.text += run.text
    else acc.push({ ...run })
    return acc
  }, [])
}

/** Paragraph blocks, in document order. */
export function paragraphNodes(content: unknown): RichNode[] {
  return blocksOf(content).filter((n) => n.type === 'paragraph')
}

/** Paragraph text, trimmed, empties dropped. */
export function paragraphsOf(content: unknown): string[] {
  return paragraphNodes(content)
    .map(textOf)
    .map((t) => t.trim())
    .filter(Boolean)
}

/** Heading text, optionally at one level only. */
export function headingsOf(content: unknown, level?: number): string[] {
  return blocksOf(content)
    .filter((n) => n.type === 'heading' && (level === undefined || levelOf(n) === level))
    .map(textOf)
    .map((t) => t.trim())
    .filter(Boolean)
}

/** The first heading's text, at any level. */
export function firstHeadingOf(content: unknown): string | undefined {
  return headingsOf(content)[0]
}

export type ListKind = 'bullet' | 'numbered'

const LIST_CONTAINER: Record<ListKind, string> = { bullet: 'bulletList', numbered: 'orderedList' }
const LIST_ITEM: Record<ListKind, string> = { bullet: 'bulletListItem', numbered: 'numberedListItem' }

/**
 * List item text — and this one is why `blocksOf` alone was never enough.
 *
 * The two formats disagree about list STRUCTURE, not just about wrappers.
 * Tiptap nests `bulletList > listItem > paragraph > text`; BlockNote has no list
 * container at all and emits each item as its own top-level `bulletListItem`,
 * with nesting expressed through `children`. A reader that only unwrapped the
 * document container still returned zero items for anything a client typed.
 *
 * That gap survived the 2026-07-31 patch because the site it was written for has
 * no bullet lists. Sam's collaborators and milestones, and Coastal's repertoire,
 * are the content it would have silently emptied.
 *
 * Items are collected in document order, so a page holding both shapes reads
 * back in the order it renders.
 */
export function listItemsOf(content: unknown, kind: ListKind = 'bullet'): string[] {
  const out: string[] = []

  const pushItem = (item: RichNode) => {
    out.push(textOf(item))
    // BlockNote nests by `children`; those are real items the client typed.
    for (const child of item.children ?? []) pushItem(child)
  }

  for (const block of blocksOf(content)) {
    if (block.type === LIST_CONTAINER[kind]) {
      for (const item of block.content ?? []) pushItem(item)
    } else if (block.type === LIST_ITEM[kind]) {
      pushItem(block)
    }
  }

  return out.map((t) => t.trim()).filter(Boolean)
}

/** Bullet list item text, from either format. */
export function bulletItemsOf(content: unknown): string[] {
  return listItemsOf(content, 'bullet')
}

/**
 * Everything a site can read out of a rich-text value — the canonical
 * "what would the page show?" projection.
 *
 * This exists so that the round-trip GATE and the DATA MIGRATION assert the same
 * property. They are the two places that ask "did converting this change what
 * the client sees?", and if each computed its own answer the migration could
 * report success against a weaker definition than the gate enforces. One
 * definition, both callers.
 *
 * Deliberately NOT block-level. Tiptap models a list as one container and
 * BlockNote as N siblings, so block sequence legitimately changes under
 * conversion; asserting on it would be asserting the two formats are one format.
 * Runs are taken over paragraphs, which ARE preserved one-for-one.
 *
 * Adding a reader to this package means adding it here, or the protection
 * silently stops covering it.
 */
export function siteView(content: unknown) {
  return {
    paragraphs: paragraphsOf(content),
    headings: headingsOf(content),
    headingsByLevel: [1, 2, 3, 4, 5, 6].map((level) => headingsOf(content, level)),
    bulletItems: bulletItemsOf(content),
    numberedItems: listItemsOf(content, 'numbered'),
    paragraphRuns: paragraphNodes(content).map((p) => runsOf(p)),
    hasText: hasText(content),
  }
}
